/**
 * Detects a long-press on an element. Wraps both mouse and touch event
 * handlers so the same component works on desktop and iPad/phone.
 *
 * Design notes:
 *  - `onHoldStart` / `onHoldEnd` fire on press start and cancel — components
 *    use these to render a subtle "we're listening" visual (per spec §7,
 *    gentle wobble or growing ring).
 *  - The timer is cancelled by pointer-up, pointer-leave, or a touch
 *    cancellation. No partial-fire — either the full duration elapses and
 *    `onLongPress` runs, or it doesn't.
 *  - Touch handling: callers should also set `onContextMenu={preventDefault}`
 *    and CSS `user-select: none; -webkit-touch-callout: none;` on the
 *    target, otherwise iOS Safari will show its long-press callout / text
 *    selection halfway through the hold.
 */
import { useCallback, useEffect, useRef } from "react";

const DEFAULT_HOLD_MS = 2000; // spec §10 default

export interface UseLongPressOptions {
  holdMs?: number;
  onHoldStart?: () => void;
  onHoldEnd?: () => void;
}

export interface LongPressHandlers {
  onMouseDown: () => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: () => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

export function useLongPress(
  onLongPress: () => void,
  options: UseLongPressOptions = {},
): LongPressHandlers {
  const { holdMs = DEFAULT_HOLD_MS, onHoldStart, onHoldEnd } = options;
  const timerRef = useRef<number | null>(null);
  const heldRef = useRef(false);

  const start = useCallback(() => {
    if (timerRef.current !== null) return; // already pressing
    heldRef.current = true;
    onHoldStart?.();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      heldRef.current = false;
      onLongPress();
    }, holdMs);
  }, [holdMs, onLongPress, onHoldStart]);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (heldRef.current) {
      heldRef.current = false;
      onHoldEnd?.();
    }
  }, [onHoldEnd]);

  // Clean up if the component unmounts mid-press.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    onMouseDown: start,
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchCancel: cancel,
  };
}
